import type { Zone } from '@seek/audience-zones';
import type { useField } from '@seek/forms-ui';
import { ProfileAutosuggest } from '@seek/profile-edit-ui';

import { PREFERRED_LOCATIONS } from 'src/components/Router';
import { useFocus } from 'src/modules/hooks/useFocus';
import type * as Schema from 'src/types/graphql';

import type { FormValues } from '../EditPreferredLocation';

import { findDuplicateLocation } from './validation';

type PreferredLocationField = ReturnType<
  typeof useField<FormValues, 'preferredLocation', any, any>
>;

export interface AutosuggestItem {
  text: string;
  value?: number;
  shortName?: string;
  countryCode?: string;
  kind?: string;
}

interface AddLocationProps {
  addNewLocation: (location: Schema.Location2) => void;
  locations: Schema.Location2[];
  zone: Zone;
  preferredLocationField: PreferredLocationField;
  resetForm: () => void;
}

export const AddLocation = ({
  addNewLocation,
  locations,
  zone,
  preferredLocationField,
  resetForm,
}: AddLocationProps) => {
  const preferredLocationsRef = useFocus<HTMLInputElement>({
    card: PREFERRED_LOCATIONS,
    fieldName: 'preferred-locations',
  });

  const handleAdd = (formValues: { preferredLocation: AutosuggestItem }) => {
    if (formValues.preferredLocation.value) {
      const {
        value: id,
        shortName,
        countryCode,
        text,
        kind,
      } = formValues.preferredLocation;
      addNewLocation({
        id,
        shortDescription: shortName || '',
        description: text || '',
        countryCode: countryCode || '',
        kind: kind || '',
        isDeprecated: false,
      });
    }
  };

  const handleOnSelect = (location: AutosuggestItem) => {
    const isDuplicate = findDuplicateLocation(location, locations);

    if (isDuplicate) {
      // Trigger valdations to run on the selected value to show error message
      preferredLocationField.onBlur();
    } else {
      // Reset the form back to its initial state
      resetForm();
      // Add the selected location to the list
      handleAdd({ preferredLocation: location });
    }
  };

  const getStructuredData = ({
    value,
    shortName,
    text,
    countryCode,
    kind,
  }: AutosuggestItem) => ({
    value,
    shortName,
    text,
    countryCode,
    kind,
  });

  return (
    <ProfileAutosuggest
      inputRef={preferredLocationsRef}
      label=""
      id="preferredLocation"
      asset="ProfileLocation"
      profileLocationProps={{
        zone,
        locationType: 'Preferred',
      }}
      getStructuredData={getStructuredData}
      showNoResultsMessage={true}
      data={{ automation: 'preferred-location-input' }}
      {...preferredLocationField}
      onSuggestionSelect={handleOnSelect}
    />
  );
};
